import { EMPTY_FIELD } from 'constants/emptyField';

import CopyAllIcon from '@mui/icons-material/CopyAll';
import { SxProps } from '@mui/material';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import get from 'lodash/get';
import { useRecordContext } from 'react-admin';
import { Link as RouterLink, To } from 'react-router-dom';
import { IconButton } from 'shared/mui/IconButtons';
import { Link } from 'shared/mui/Link/Link';
import { Tooltip } from 'shared/mui/Tooltip';
import {
  FunctionField,
  FunctionFieldProps,
} from 'shared/react-admin/Fields/FunctionField/FunctionField';

import { BoxStyled, TypographyStyled } from './CopyToClipboardLinkField.styled';

export type CopyToClipboardLinkFieldProps = {
  source: string;
  getLink: (record: any) => To;
  getValue?: (value: any) => string;
  isTrimValue?: boolean;
  target?: string;
  sx?: SxProps;
} & Omit<FunctionFieldProps, 'render' | 'sx'>;

const MAX_VALUE_LENGTH = 12;

export const CopyToClipboardLinkField: React.FC<CopyToClipboardLinkFieldProps> = ({
  onClick,
  getValue,
  getLink,
  source,
  sx,
  isTrimValue,
  target = '_self',
  ...rest
}) => {
  const record = useRecordContext();
  const recordValue = get(record, source);
  const normalizedValue = getValue ? getValue(recordValue) : recordValue;
  const shouldTrim = isTrimValue && normalizedValue?.length > MAX_VALUE_LENGTH;
  const trimmedValue = shouldTrim
    ? `${normalizedValue.slice(0, 6)}...${normalizedValue.slice(-4)}`
    : null;

  const { tooltipText, handleCopy } = useCopyToClipboard(normalizedValue);

  if (!record || !recordValue) {
    return <span>{EMPTY_FIELD}</span>;
  }

  const renderLink = (value: string) => (
    <Link
      color="primary"
      component={RouterLink}
      target={target}
      to={getLink(record)}
      underline="hover"
    >
      <TypographyStyled sx={sx} variant="body2">
        {value}
      </TypographyStyled>
    </Link>
  );

  return (
    <FunctionField
      render={() => (
        <BoxStyled onClick={onClick}>
          {trimmedValue ? (
            <Tooltip arrow title={normalizedValue}>
              {renderLink(trimmedValue)}
            </Tooltip>
          ) : (
            renderLink(normalizedValue)
          )}
          <Tooltip TransitionProps={{ timeout: 0 }} open={!!tooltipText} title={tooltipText}>
            <IconButton onClick={handleCopy} size="small">
              <CopyAllIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </BoxStyled>
      )}
      {...rest}
    />
  );
};
